import { Page } from '@app/components/page/Page';
import { GroupIcon } from '@app/components/users/GroupIcon';
import { deleteGroup } from '@app/modules/admin/api';
import { GroupActionPermissionsView } from '@app/modules/admin/components/actions-permissions/GroupActionPermissionsView';
import { DeleteButton } from '@app/modules/admin/components/DeleteButton';
import { EditButton } from '@app/modules/admin/components/EditButton';
import { useGroupQuery } from '@app/modules/admin/screens/groups/hooks/useGroupQuery';
import { capitalizeFirst } from '@mi-front/core';
import { ParamDrivenTabsMui } from '@mi-front/ui';
import HelpIcon from '@mui/icons-material/Help';
import { Box, Stack, Tooltip } from '@mui/material';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { GroupsBreadcrumbs } from '../header';
import { GroupInfo } from './tabs/GroupInfo';
import { GroupPermissionsManager } from './tabs/GroupPermissionsManager';

export enum TabKey {
  INFO = 'info',
  PERMISSIONS = 'permissions',
  ACTIONS_PERMISSIONS = 'actions-permissions',
}

export function ViewGroupPage() {
  const { id } = useParams<{ id: string }>();
  const { $t, formatMessage } = useIntl();
  const groupQuery = useGroupQuery(id);

  return (
    <QueryView query={groupQuery}>
      {(group) => {
        const canDelete = group.users.length === 0;
        const tabs = [
          {
            value: TabKey.INFO,
            label: (
              <FormattedMessage
                id="admin.groups.viewScreen.basicInfoTab"
                defaultMessage="Información"
              />
            ),
            content: <GroupInfo group={group} />,
          },
          {
            value: TabKey.PERMISSIONS,
            label: (
              <FormattedMessage
                id="admin.groups.viewScreen.stationsPermissionsTab"
                defaultMessage="Acceso a emisoras"
              />
            ),
            content: <GroupPermissionsManager group={group} />,
          },
          {
            value: TabKey.ACTIONS_PERMISSIONS,
            label: (
              <FormattedMessage
                id="admin.groups.viewScreen.actionsPermissionsTab"
                defaultMessage="Permisos"
              />
            ),
            content: <GroupActionPermissionsView group={group} />,
          },
        ];

        return (
          <Page
            data-cy="groups-view-screen"
            htmlTitle={`${capitalizeFirst(
              $t({
                id: 'common.entities.group',
                defaultMessage: 'grupo',
              }),
            )} ${group.name}`}
          >
            <Page.Header
              icon={<GroupIcon />}
              title={
                <>
                  <FormattedMessage id="common.entities.group" defaultMessage="grupo">
                    {(text) => (
                      <Box component="span" sx={{ textTransform: 'capitalize' }}>
                        {text}
                      </Box>
                    )}
                  </FormattedMessage>
                  &nbsp;
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {group.name}
                  </Box>
                </>
              }
              breadcrumbs={<GroupsBreadcrumbs />}
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  <EditButton to={`/admin/groups/${group.id}/edit`} />
                  {!canDelete && (
                    <Tooltip
                      title={
                        <FormattedMessage
                          id="admin.groups.viewScreen.notDeletableHelp"
                          defaultMessage="Este grupo no se puede eliminar porque contiene usuarios asignados"
                        />
                      }
                    >
                      <HelpIcon fontSize="small" color="action" />
                    </Tooltip>
                  )}
                  <DeleteButton
                    onDelete={() => deleteGroup(group.id)}
                    title={formatMessage({
                      id: 'admin.groups.deleteScreen.title',
                      defaultMessage: 'Eliminar grupo',
                    })}
                    message={
                      <FormattedMessage
                        id="admin.groups.deleteScreen.prompt"
                        defaultMessage={`Deseas realmente eliminar el grupo <b>{name}</b>? {br} Esta acción no se puede deshacer.`}
                        values={{
                          name: group.name,
                          b: (chunks) => <strong>{chunks}</strong>,
                          br: <br />,
                        }}
                      />
                    }
                    successMessage={
                      <FormattedMessage
                        id="admin.groups.notifications.deleted"
                        defaultMessage="Grupo eliminado"
                      />
                    }
                    disabled={!canDelete}
                    redirectTo="/admin/groups"
                  />
                </Stack>
              }
            />
            <Page.Content>
              <ParamDrivenTabsMui id="group-view-tabs" tabs={tabs} />
            </Page.Content>
          </Page>
        );
      }}
    </QueryView>
  );
}
